import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import {
  Award,
  AlertCircle,
  BookOpen,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Flame,
  Inbox,
  LogOut,
  Mail,
  MailCheck,
  MailX,
  Play,
  RefreshCw,
  XCircle,
} from "lucide-react";
import { Onboarding, adminApi, tokenStorage } from "@/lib/api";
import Logo from "@/components/Logo";

const STEP_META = {
  day1_documentation: { label: "Day 1 · Documentation", color: "text-amber-200 border-amber-400/30 bg-amber-400/10" },
  day3_training:      { label: "Day 3 · Training",      color: "text-blue-200  border-blue-400/30  bg-blue-400/10" },
  day7_first_steps:   { label: "Day 7 · First Steps",   color: "text-violet-200 border-violet-400/30 bg-violet-400/10" },
  day11_checkin:      { label: "Day 11 · Check-in",     color: "text-emerald-200 border-emerald-400/30 bg-emerald-400/10" },
};

const PAGE_SIZE = 25;

function StatusBadge({ res }) {
  if (res?.sent === true) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-400/15 border border-emerald-400/35 text-emerald-200 text-[11px]">
        <MailCheck size={11} /> Sent
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-rose-400/15 border border-rose-400/35 text-rose-200 text-[11px]">
      <MailX size={11} /> No-op
    </span>
  );
}

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminOnboardingLog() {
  const navigate = useNavigate();
  const hasToken = Boolean(tokenStorage.get());

  const [items, setItems] = useState([]);
  const [byStep, setByStep] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [stepFilter, setStepFilter] = useState("");
  const [sentFilter, setSentFilter] = useState(""); // "" | "true" | "false"
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState(null);
  const [resendingKey, setResendingKey] = useState(null); // `${partner_id}-${step_key}`
  const [resendNotice, setResendNotice] = useState(null);
  const [providerStatus, setProviderStatus] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, skip: page * PAGE_SIZE };
      if (stepFilter) params.step = stepFilter;
      if (sentFilter !== "") params.sent_only = sentFilter;
      const [data, status] = await Promise.all([
        Onboarding.log(params),
        Onboarding.providerStatus().catch(() => null),
      ]);
      setItems(data.items || []);
      setByStep(data.by_step || []);
      setTotal(data.total || 0);
      setProviderStatus(status);
    } finally {
      setLoading(false);
    }
  }, [page, stepFilter, sentFilter]);

  useEffect(() => {
    if (hasToken) load();
  }, [load, hasToken]);

  const runDrip = async () => {
    setRunning(true);
    setRunResult(null);
    try {
      const r = await Onboarding.runNow();
      setRunResult(r);
      // Drip now runs as a background task on the backend (to avoid Cloudflare
      // 100 s timeouts on production when there are many partners). Give it a
      // few seconds before reloading so freshly-written log rows show up.
      setTimeout(load, r?.queued ? 6000 : 300);
    } catch (e) {
      setRunResult({ ok: false, error: e?.response?.data?.detail || e.message });
    } finally {
      setRunning(false);
    }
  };

  const resendOne = async (row) => {
    if (!row.partner_id || !row.step_key) return;
    const ok = window.confirm(
      `Resend "${STEP_META[row.step_key]?.label || row.step_key}" to ${row.partner_email || row.partner_name}?`
    );
    if (!ok) return;
    const key = `${row.partner_id}-${row.step_key}`;
    setResendingKey(key);
    setResendNotice(null);
    try {
      const r = await Onboarding.resend(row.partner_id, row.step_key);
      setResendNotice({
        ok: true,
        text: r?.dispatch_result?.sent
          ? `Resent to ${row.partner_email} via ${r.dispatch_result.provider}`
          : `Re-dispatched (no-op): ${r?.dispatch_result?.reason || "EMAIL_PROVIDER off"}`,
      });
      await load();
    } catch (e) {
      setResendNotice({ ok: false, text: e?.response?.data?.detail || e.message });
    } finally {
      setResendingKey(null);
    }
  };

  const logout = async () => {
    try { await adminApi.post("/auth/logout"); }
    catch { /* best-effort server logout; local session is cleared regardless */ }
    tokenStorage.clear();
    navigate("/admin", { replace: true });
  };

  if (!hasToken) return <Navigate to="/admin" replace />;

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const stepTotals = Object.fromEntries(byStep.map((s) => [s._id, s]));

  return (
    <div className="min-h-screen bg-[#04102B] text-white">
      <header className="sticky top-0 z-30 bg-[#04102B]/95 backdrop-blur border-b border-white/10">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-5 md:px-8 py-3">
          <Link to="/" className="flex items-center gap-3">
            <Logo size={36} />
            <div className="leading-tight">
              <div className="text-sm font-semibold text-white">Sahyog Admin</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">Onboarding Log</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <a href="/admin/change-password" data-testid="admin-change-pw-link" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#D4AF37]/40 hover:bg-[#D4AF37]/10 text-[#F0D788] text-[11px] font-semibold">Change password</a>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/15 hover:border-rose-400/40 text-xs text-white/80"
            >
              <LogOut size={12} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-8 md:py-10">
        {/* Nav tabs */}
        <section className="mb-8">
          <div className="flex flex-wrap gap-2">
            <Link to="/admin/leads" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.05] border border-white/10 hover:border-[#D4AF37]/40 text-sm"><Inbox size={14} /> Leads</Link>
            <Link to="/admin/applications" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.05] border border-white/10 hover:border-[#D4AF37]/40 text-sm"><Award size={14} /> Applications</Link>
            <Link to="/admin/grievances" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.05] border border-white/10 hover:border-[#D4AF37]/40 text-sm"><AlertCircle size={14} /> Grievances</Link>
            <Link to="/admin/offers" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.05] border border-white/10 hover:border-[#D4AF37]/40 text-sm"><Flame size={14} /> Hot Offers</Link>
            <Link to="/admin/blogs" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.05] border border-white/10 hover:border-[#D4AF37]/40 text-sm"><BookOpen size={14} /> Blogs</Link>
            <span data-testid="nav-tab-onboarding" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37] text-[#0A1F44] border border-[#D4AF37] text-sm font-semibold">
              <Mail size={14} /> Onboarding Log
            </span>
          </div>
        </section>

        {/* Header strip */}
        <section className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-1">Partner Drip · Dispatch Log</div>
            <h1 className="font-serif text-3xl md:text-4xl text-white">Onboarding Email Log</h1>
            <p className="mt-2 text-sm text-white/65 max-w-2xl">
              Every onboarding email the scheduler attempted (or no-op'd while Resend was off).
              Each (partner, step) is logged once — idempotent by design.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              data-testid="onboarding-refresh"
              onClick={load}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 text-sm text-white/85 hover:border-[#D4AF37]/40"
            >
              <RefreshCw size={13} /> Refresh
            </button>
            <button
              data-testid="onboarding-run-now"
              onClick={runDrip}
              disabled={running}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37] text-[#0A1F44] text-sm font-semibold disabled:opacity-60"
            >
              <Play size={13} /> {running ? "Running…" : "Run drip now"}
            </button>
          </div>
        </section>

        {providerStatus && (
          <div
            data-testid="onboarding-provider-status"
            className={`mb-6 rounded-xl border px-4 py-3 text-xs ${
              providerStatus.healthy
                ? "border-emerald-400/35 bg-emerald-500/8 text-emerald-100"
                : "border-rose-400/45 bg-rose-500/8 text-rose-100"
            }`}
          >
            <div className="flex items-center gap-2 mb-1 font-semibold">
              {providerStatus.healthy ? (
                <><MailCheck size={14} className="text-emerald-300" /> Email provider is healthy</>
              ) : (
                <><AlertCircle size={14} className="text-rose-300" /> Email provider is DISABLED — drips will no-op</>
              )}
              <span className="ml-auto text-white/50 text-[10px] uppercase tracking-wider">
                Resolved: <strong className={providerStatus.healthy ? "text-emerald-200" : "text-rose-200"}>{providerStatus.resolved_provider}</strong>
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-[11px] text-white/70 mb-2">
              <div>EMAIL_PROVIDER = <span className="font-mono text-white/90">{providerStatus.env?.EMAIL_PROVIDER}</span></div>
              <div>RESEND_API_KEY set = <span className={providerStatus.env?.RESEND_API_KEY_set ? "text-emerald-300 font-semibold" : "text-rose-300 font-semibold"}>{providerStatus.env?.RESEND_API_KEY_set ? "yes" : "NO"}</span></div>
              <div>RESEND_FROM_EMAIL set = <span className={providerStatus.env?.RESEND_FROM_EMAIL_set ? "text-emerald-300 font-semibold" : "text-amber-300 font-semibold"}>{providerStatus.env?.RESEND_FROM_EMAIL_set ? "yes" : "no (using default)"}</span></div>
            </div>
            {providerStatus.hints?.length > 0 && (
              <ul className="mt-2 space-y-1 text-[11px] text-white/85 leading-relaxed">
                {providerStatus.hints.map((h, i) => (
                  <li key={`hint-${i}-${String(h).slice(0, 32)}`} className="flex gap-2">
                    <span className="text-amber-300 shrink-0">→</span>
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {runResult && (
          <div data-testid="onboarding-run-result" className="mb-6 rounded-xl border border-[#D4AF37]/25 bg-[#0A1F44] px-4 py-3 text-xs text-white/80 flex flex-wrap items-center gap-4">
            {runResult.ok ? (
              <>
                <CheckCircle2 size={14} className="text-emerald-300" />
                <span>Ran at <strong className="text-white">{fmtTime(runResult.ran_at)}</strong></span>
                <span>Sent: <strong className="text-emerald-300">{runResult.sent}</strong></span>
                <span>Skipped: <strong className="text-white">{runResult.skipped}</strong></span>
                <span>Errors: <strong className={runResult.errors ? "text-rose-300" : "text-white"}>{runResult.errors}</strong></span>
              </>
            ) : (
              <><XCircle size={14} className="text-rose-300" /> {runResult.error || "Run failed"}</>
            )}
          </div>
        )}

        {resendNotice && (
          <div
            data-testid="onboarding-resend-notice"
            className={`mb-6 rounded-xl border px-4 py-3 text-xs flex items-center gap-3 ${
              resendNotice.ok
                ? "bg-emerald-400/10 border-emerald-400/35 text-emerald-100"
                : "bg-rose-400/10 border-rose-400/35 text-rose-100"
            }`}
          >
            {resendNotice.ok ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
            {resendNotice.text}
            <button
              onClick={() => setResendNotice(null)}
              className="ml-auto opacity-70 hover:opacity-100 underline-offset-2 hover:underline"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Step totals */}
        <section className="mb-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {Object.entries(STEP_META).map(([key, m]) => {
            const s = stepTotals[key] || { total: 0, sent: 0 };
            return (
              <button
                key={key}
                data-testid={`step-tile-${key}`}
                onClick={() => { setStepFilter(stepFilter === key ? "" : key); setPage(0); }}
                className={`text-left rounded-2xl border bg-[#0A1F44] p-4 transition ${stepFilter === key ? "border-[#D4AF37]/70" : "border-white/10 hover:border-[#D4AF37]/30"}`}
              >
                <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-[0.2em] ${m.color}`}>
                  <Clock size={10} /> {m.label}
                </div>
                <div className="mt-3 font-serif text-3xl text-white">{s.total}</div>
                <div className="mt-1 text-xs text-white/60">
                  Sent <span className="text-emerald-300">{s.sent}</span> · Pending {s.total - s.sent}
                </div>
              </button>
            );
          })}
        </section>

        {/* Filters */}
        <section className="mb-4 flex flex-wrap gap-2 items-center text-xs">
          <span className="text-white/45 uppercase tracking-[0.18em]">Filter:</span>
          {stepFilter && (
            <button
              data-testid="filter-clear-step"
              onClick={() => { setStepFilter(""); setPage(0); }}
              className="px-3 py-1.5 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/35 text-[#F0D788]"
            >
              Step: {STEP_META[stepFilter]?.label} · clear ×
            </button>
          )}
          <select
            data-testid="filter-sent"
            value={sentFilter}
            onChange={(e) => { setSentFilter(e.target.value); setPage(0); }}
            className="px-3 py-1.5 rounded-full bg-[#0A1F44] border border-white/15 text-white/85 text-xs outline-none"
          >
            <option value="">All status</option>
            <option value="true">Sent only</option>
            <option value="false">No-op / failed</option>
          </select>
          <span className="ml-auto text-white/55">
            {total} total · page {page + 1} / {pages}
          </span>
        </section>

        {/* Log table */}
        <section data-testid="onboarding-log-table" className="rounded-2xl border border-white/10 overflow-hidden bg-[#0A1F44]/40">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[920px]">
              <thead className="bg-white/[0.04] text-[11px] uppercase tracking-[0.18em] text-white/55">
                <tr>
                  <th className="text-left px-4 py-3">Partner</th>
                  <th className="text-left px-4 py-3">Step</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Reason / Provider</th>
                  <th className="text-left px-4 py-3">Due at</th>
                  <th className="text-left px-4 py-3">Sent at</th>
                  <th className="text-right px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-white/55">Loading…</td></tr>
                )}
                {!loading && items.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-white/55">
                    No log entries yet. Once partners register and the scheduler ticks, you'll see entries here.
                  </td></tr>
                )}
                {!loading && items.map((row, i) => {
                  const step = STEP_META[row.step_key] || { label: row.step_key };
                  const rowKey = `${row.partner_id}-${row.step_key}`;
                  const isResending = resendingKey === rowKey;
                  return (
                    <tr
                      key={`${row.partner_id}-${row.step_key}-${i}`}
                      data-testid={`onb-row-${i}`}
                      className="border-t border-white/5 hover:bg-white/[0.025]"
                    >
                      <td className="px-4 py-3">
                        <div className="text-white text-sm">{row.partner_name || "—"}</div>
                        <div className="text-[11px] text-white/50">
                          {row.partner_role?.toUpperCase() || ""} · {row.partner_email}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full border text-[11px] ${step.color || "text-white/70 border-white/15 bg-white/[0.05]"}`}>
                          {step.label}
                        </span>
                      </td>
                      <td className="px-4 py-3"><StatusBadge res={row.dispatch_result} /></td>
                      <td className="px-4 py-3 text-xs text-white/65">
                        {row.dispatch_result?.provider || "—"}
                        {row.dispatch_result?.reason && (
                          <div className="text-[11px] text-white/45 mt-0.5 italic">
                            {row.dispatch_result.reason}
                          </div>
                        )}
                        {row.dispatch_result?.id && (
                          <div className="text-[11px] text-emerald-300 font-mono mt-0.5">
                            #{row.dispatch_result.id.slice(0, 12)}
                          </div>
                        )}
                        {row.dispatch_result?.resent && (
                          <div className="text-[11px] text-amber-300 mt-0.5">manual resend</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-white/65">{fmtTime(row.due_at)}</td>
                      <td className="px-4 py-3 text-xs text-white/65">{fmtTime(row.sent_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          data-testid={`onb-resend-${i}`}
                          onClick={() => resendOne(row)}
                          disabled={isResending || !row.partner_email}
                          title={!row.partner_email ? "Partner has no email" : "Resend this email"}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#D4AF37]/35 hover:border-[#D4AF37]/70 text-[11px] text-[#F0D788] disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {isResending ? <RefreshCw size={11} className="animate-spin" /> : <Mail size={11} />}
                          {isResending ? "Sending…" : "Resend"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-white/5 text-xs text-white/70">
              <button
                data-testid="onb-prev"
                disabled={page === 0}
                onClick={() => setPage(page - 1)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/15 disabled:opacity-40"
              >
                <ChevronLeft size={12} /> Prev
              </button>
              <span>Page {page + 1} of {pages}</span>
              <button
                data-testid="onb-next"
                disabled={page + 1 >= pages}
                onClick={() => setPage(page + 1)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/15 disabled:opacity-40"
              >
                Next <ChevronRight size={12} />
              </button>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
